import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'date-range';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

interface FilterBarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  filters?: FilterConfig[];
  activeFilters: Record<string, any>;
  onFilterChange: (filterKey: string, value: any) => void;
  onClearFilters: () => void;
}

export function FilterBar({
  searchPlaceholder = 'Search...',
  searchValue,
  onSearchChange,
  filters = [],
  activeFilters,
  onFilterChange,
  onClearFilters,
}: FilterBarProps) {
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchValue);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(debouncedSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [debouncedSearch, onSearchChange]);

  const activeFilterCount = Object.values(activeFilters).filter(
    (value) => value !== undefined && value !== null && value !== ''
  ).length;

  const hasActiveFilters = activeFilterCount > 0 || searchValue;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={debouncedSearch}
            onChange={(e) => setDebouncedSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {filters.map((filter) => (
          <div key={filter.key} className="min-w-[200px]">
            {filter.type === 'select' ? (
              <Select
                value={activeFilters[filter.key] || ''}
                onValueChange={(value) => onFilterChange(filter.key, value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={filter.placeholder || filter.label} />
                </SelectTrigger>
                <SelectContent>
                  {filter.options?.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : filter.type === 'date-range' ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !activeFilters[filter.key] && 'text-muted-foreground'
                    )}
                  >
                    {activeFilters[filter.key]?.from && activeFilters[filter.key]?.to
                      ? `${format(activeFilters[filter.key].from, 'PPP')} - ${format(
                          activeFilters[filter.key].to,
                          'PPP'
                        )}`
                      : filter.placeholder || filter.label}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={activeFilters[filter.key]}
                    onSelect={(range) => onFilterChange(filter.key, range)}
                    numberOfMonths={2}
                    className={cn('p-3 pointer-events-auto')}
                  />
                </PopoverContent>
              </Popover>
            ) : null}
          </div>
        ))}

        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={onClearFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {searchValue && (
            <Badge variant="secondary" className="gap-1">
              Search: {searchValue}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => {
                  setDebouncedSearch('');
                  onSearchChange('');
                }}
              />
            </Badge>
          )}
          {filters.map((filter) => {
            const value = activeFilters[filter.key];
            if (!value) return null;

            let displayValue = '';
            if (filter.type === 'select') {
              displayValue = filter.options?.find((opt) => opt.value === value)?.label || value;
            } else if (filter.type === 'date-range' && value.from && value.to) {
              displayValue = `${format(value.from, 'PP')} - ${format(value.to, 'PP')}`;
            }

            return displayValue ? (
              <Badge key={filter.key} variant="secondary" className="gap-1">
                {filter.label}: {displayValue}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => onFilterChange(filter.key, undefined)}
                />
              </Badge>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}
